import React, { useState, useMemo } from 'react'
import { Modal, Button, Table, Spinner, Badge, Form, Alert } from 'react-bootstrap'
import { useQuery } from '@apollo/client'
import {
  GET_CARRIER_IMPORT_BATCH,
  GET_CARRIER_IMPORT_BATCH_UNMATCHED,
} from '../../../gql/queries/carrierImportQueries'
import moment from 'moment'
import {BadgeStatePolicy} from '../../onePolicy/components/BadgeStatePolicy'
import {exportUnmatchedCsv, CarrierImportUnmatchedRow} from './carrierImportExport'

interface ModalViewCarrierImportBatchProps {
  show: boolean
  onClose: () => void
  batchId: string
  policyStatuses: {code: string; title: string; color?: string}[]
}

export const ModalViewCarrierImportBatch: React.FC<ModalViewCarrierImportBatchProps> = ({
  show,
  onClose,
  batchId,
  policyStatuses,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const rowsPerPage = 25

  const { data: batchData, loading: batchLoading } = useQuery(GET_CARRIER_IMPORT_BATCH, {
    variables: { id: batchId },
    skip: !batchId,
  })

  const { data: unmatchedData, loading: unmatchedLoading } = useQuery(
    GET_CARRIER_IMPORT_BATCH_UNMATCHED,
    {
      variables: { batchId },
      skip: !batchId,
    }
  )

  const batch = batchData?.carrierImportBatch
  const unmatchedRows: CarrierImportUnmatchedRow[] = unmatchedData?.carrierImportBatchUnmatched || []

  const headerOrder = useMemo(() => {
    const meta = batch?.metadata
    if (!meta) return undefined
    if (Array.isArray(meta)) return meta
    if (typeof meta === 'string') {
      try {
        const parsed = JSON.parse(meta)
        if (parsed && Array.isArray(parsed.headers)) return parsed.headers
      } catch {
        return undefined
      }
    }
    if (typeof meta === 'object' && Array.isArray((meta as any).headers)) {
      return (meta as any).headers
    }
    return undefined
  }, [batch?.metadata])

  const statusTitleByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.title || ''
    }
    return map
  }, [policyStatuses])

  const statusColorByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.color || 'secondary'
    }
    return map
  }, [policyStatuses])

  // Filter rows based on search query
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return unmatchedRows

    const query = searchQuery.toLowerCase()
    return unmatchedRows.filter(
      (row) =>
        row.memberId?.toLowerCase().includes(query) || row.name?.toLowerCase().includes(query)
    )
  }, [unmatchedRows, searchQuery])

  // Paginate filtered rows
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage)
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, currentPage])

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1) // Reset to first page when searching
  }

  return (
    <Modal show={show} onHide={onClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>Carrier Import Batch Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {batchLoading ? (
          <div className='d-flex justify-content-center p-4'>
            <Spinner animation='border' />
          </div>
        ) : batch ? (
          <>
            <div className='mb-4'>
              <h6>Batch Information</h6>
              <div className='row'>
                <div className='col-md-6'>
                  <p>
                    <strong>Carrier:</strong> {batch.carrier.name}
                  </p>
                  <p>
                    <strong>As-of Date:</strong> {batch.asOfDate || 'N/A'}
                  </p>
                  <p>
                    <strong>Filename:</strong> {batch.originalFilename}
                  </p>
                </div>
                <div className='col-md-6'>
                  <p>
                    <strong>Uploaded:</strong> {moment(batch.uploadedAt).format('MM/DD/YYYY hh:mm A')}
                  </p>
                  <p>
                    <strong>Uploaded By:</strong> {batch.uploadedBy.firstName}{' '}
                    {batch.uploadedBy.lastName}
                  </p>
                </div>
              </div>

              <div className='d-flex gap-3 mb-3'>
                <div>
                  <strong>Processed:</strong>{' '}
                  <Badge bg='secondary'>{batch.processedCount}</Badge>
                </div>
                <div>
                  <strong>Matched:</strong> <Badge bg='success'>{batch.matchedCount}</Badge>
                </div>
                <div>
                  <strong>Unmatched:</strong>{' '}
                  <Badge bg='warning' text='dark'>
                    {batch.unmatchedCount}
                  </Badge>
                </div>
                <div>
                  <strong>Errors:</strong> <Badge bg='danger'>{batch.errorsCount}</Badge>
                </div>
              </div>
            </div>

            {unmatchedLoading ? (
              <div className='d-flex justify-content-center p-4'>
                <Spinner animation='border' size='sm' />
              </div>
            ) : unmatchedRows.length > 0 ? (
              <>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='mb-0'>
                    Unmatched Rows ({filteredRows.length}
                    {searchQuery.trim() ? ` of ${unmatchedRows.length}` : ''})
                  </h6>
                  <Button
                    variant='outline-primary'
                    size='sm'
                    onClick={() =>
                      exportUnmatchedCsv(
                        unmatchedRows,
                        statusTitleByCode,
                        `carrier_import_batch_${batchId}_unmatched`,
                        headerOrder
                      )
                    }
                  >
                    Export Unmatched CSV
                  </Button>
                </div>

                <Form.Control
                  type='text'
                  placeholder='Search by Member ID or Name...'
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className='mb-2'
                  size='sm'
                />

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <Table striped bordered hover size='sm'>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Member ID</th>
                        <th>Name</th>
                        <th>Carrier Status</th>
                        <th>System Status</th>
                        <th>Agent</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.map((row, idx) => (
                        <tr key={idx}>
                        <td>{row.rowNumber}</td>
                        <td>{row.memberId || '-'}</td>
                        <td>{row.name || '-'}</td>
                        <td>{row.carrierStatusRaw || row.carrierStatusGeneric || '-'}</td>
                        <td>
                          {row.systemPolicyStatus && statusTitleByCode[row.systemPolicyStatus] ? (
                            <BadgeStatePolicy
                              status={statusTitleByCode[row.systemPolicyStatus]}
                              color={statusColorByCode[row.systemPolicyStatus] || 'secondary'}
                            />
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {row.systemAgentName || row.systemAgentEmail ? (
                            <>
                              {row.systemAgentName || '-'}
                              {row.systemAgentEmail ? ` (${row.systemAgentEmail})` : ''}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <small>{row.reason || '-'}</small>
                        </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {totalPages > 1 && (
                  <div className='d-flex justify-content-between align-items-center mt-2'>
                    <span className='text-muted'>
                      Page {currentPage} of {totalPages}
                    </span>
                    <div>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        className='me-2'
                      >
                        Previous
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Alert variant='info'>No unmatched rows in this batch.</Alert>
            )}
          </>
        ) : (
          <Alert variant='danger'>Batch not found.</Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
